import React, { useState, useEffect } from 'react';
import { BallTriangle } from 'react-loader-spinner';
import toast from 'react-hot-toast';
import Nodatafound from 'pages/Common/Nodatafound';
import Card from 'components/card';
import testService from 'ApiService/Student/testServices';
import InputField from 'components/fields/InputField';

const StudentMockMarks = () => {
  const [loading, setLoading] = useState(true);
  const [subjects, setSubjects] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState('');
  const [mockTestDate, setMockTestDate] = useState('');
  const [marksData, setMarksData] = useState([]);
  const [classStats, setClassStats] = useState({});

  const fetchSubjects = async () => {
    try {
      const response = await testService.getStudentSubjects();
      if (response.data.success) {
        setSubjects(response.data.subjects);
      }
    } catch (error) {
      toast.error('Failed to fetch subjects');
    } finally {
      setLoading(false);
    }
  };

  const fetchMarks = async () => {
    if (!mockTestDate && !selectedSubject) return;

    try {
      setLoading(true);
      const response = await testService.getStudentMockMarks(
        selectedSubject,
        mockTestDate
      );

      if (response.data.success) {
        const transformedMarks = response.data.marks.map((mark) => ({
          ...mark,
          marks: mark.Marks,
          totalMarks: mark.TotalMarks,
        }));
        setMarksData(transformedMarks);
        setClassStats(response.data.classStats || {});
      }
    } catch (error) {
      toast.error('Failed to fetch marks');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubjects();
  }, []);

  useEffect(() => {
    if (selectedSubject || mockTestDate) {
      fetchMarks();
    }
  }, [selectedSubject, mockTestDate]);

  // Helper function to safely access class stats
  const getClassStats = (markId) => {
    const stats = classStats[markId] || {};
    return {
      average: stats.average || 0,
      rank: stats.rank || '-',
      totalStudents: stats.totalStudents || 0,
      percentile: stats.percentile || 0,
    };
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <BallTriangle color="#5431ed" />
      </div>
    );
  }

  return (
    <div className="mt-3 h-full w-full">
      <div className="h-[200px] w-full rounded-[20px] bg-gradient-to-br from-brand-400 to-brand-600" />
      <div className="mx-auto -mt-[100px] w-11/12 md:w-3/4">
        <Card extra="w-full p-4">
          {/* Filters */}
          <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="w-full">
              <label className="ml-3 text-sm font-bold text-navy-700 dark:text-white">
                Select Employee
              </label>
              <select
                value={selectedSubject}
                onChange={(e) => setSelectedSubject(e.target.value)}
                className="mt-2 w-full rounded-xl border border-gray-200 py-3 pl-2.5 pr-3 text-sm font-normal outline-none dark:!border-white/10 dark:!bg-navy-800 dark:text-white"
              >
                <option value="">Select Subject</option>
                {subjects.map((subject) => (
                  <option key={subject} value={subject}>
                    {subject}
                  </option>
                ))}
              </select>
            </div>

            <InputField
              label="Date"
              type="date"
              value={mockTestDate}
              onChange={(e) => setMockTestDate(e.target.value)}
              className="w-full rounded border px-3 py-2 dark:bg-navy-800"
            />
          </div>

          {/* Marks Display */}
          {marksData.length > 0 ? (
            <div className="space-y-6">
              {marksData.map((mark, index) => {
                const stats = getClassStats(mark._id);
                return (
                  <Card key={index} extra="p-4 bg-white dark:bg-navy-800">
                    <div className="flex flex-col space-y-4">
                      <div className="flex justify-between">
                        <h3 className="text-lg font-semibold">
                          {mark.subjectName}
                        </h3>
                        <span className="text-sm text-gray-500">
                          {new Date(mark.testDate).toLocaleDateString()}
                        </span>
                      </div>

                      {/* Score Card */}
                      <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
                        <div className="rounded-lg bg-blue-50 p-4 dark:bg-navy-700">
                          <h4 className="text-sm font-semibold">Your Score</h4>
                          {mark.status === 'Pending' ? (
                            <p className="text-lg font-medium text-yellow-600">
                              Pending
                            </p>
                          ) : (
                            <>
                              <p className="text-2xl font-bold">
                                {mark.marks}/{mark.totalMarks}
                              </p>
                              <p className="text-sm text-gray-600">
                                {((mark.marks / mark.totalMarks) * 100).toFixed(
                                  1
                                )}
                                %
                              </p>
                            </>
                          )}
                        </div>

                        <div className="rounded-lg bg-green-50 p-4 dark:bg-navy-700">
                          <h4 className="text-sm font-semibold">
                            Class Average
                          </h4>
                          {mark.status === 'Pending' ? (
                            <p className="text-lg font-medium text-yellow-600">
                              Pending
                            </p>
                          ) : (
                            <>
                              <p className="text-2xl font-bold">
                                {stats.average.toFixed(1)}/{mark.totalMarks}
                              </p>
                              <p className="text-sm text-gray-600">
                                {(
                                  (stats.average / mark.totalMarks) *
                                  100
                                ).toFixed(1)}
                                %
                              </p>
                            </>
                          )}
                        </div>

                        <div className="rounded-lg bg-yellow-50 p-4 dark:bg-navy-700">
                          <h4 className="text-sm font-semibold">Your Rank</h4>
                          {mark.status === 'Pending' ? (
                            <p className="text-lg font-medium text-yellow-600">
                              Pending
                            </p>
                          ) : (
                            <>
                              <p className="text-2xl font-bold">{stats.rank}</p>
                              <p className="text-sm text-gray-600">
                                out of {stats.totalStudents} students
                              </p>
                            </>
                          )}
                        </div>
                      </div>

                      {/* Performance Indicators */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <h4 className="mb-2 text-sm font-semibold">
                            Performance
                          </h4>
                          <span
                            className={`rounded-full px-3 py-1 text-sm ${
                              mark.marks >= mark.totalMarks * 0.35
                                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                            }`}
                          >
                            {mark.marks >= mark.totalMarks * 0.35
                              ? 'Pass'
                              : 'Need Improvement'}
                          </span>
                        </div>
                        <div>
                          <h4 className="mb-2 text-sm font-semibold">
                            Percentile
                          </h4>
                          <span className="text-sm font-medium">
                            {stats.percentile.toFixed(1)} percentile
                          </span>
                        </div>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          ) : (
            <div className="text-center text-gray-600 dark:text-gray-400">
              <Nodatafound title="No marks found for selected filters." />
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default StudentMockMarks;
